import {get, isEmpty, isNil, flatten, sortBy} from 'lodash'
import {getContactName, hasContactMasterLicence, sortContacts} from 'helpers/contacts'

export const TEAM_PREFIX = 'team-'
export const CONTACT_PREFIX = 'contact-'

export const WHOLE_COMPANY_TEAM_ID = 0

export const isWholeCompanyManager = (managerTeamId) => {
  return managerTeamId === WHOLE_COMPANY_TEAM_ID
}

export const isWholeCompanyTeamOrUserOption = teamOrUserOption => {
  return teamOrUserOption === `${TEAM_PREFIX}${WHOLE_COMPANY_TEAM_ID}`
}
export const UnallocatedTeamOption = {
  value: 'unallocated',
  label: 'Unallocated',
}

export const getTeamsAndUsersOptions = (user, teams, teamMembers, homeContacts, pluralNoun, showUnallocated = false) => {
  const myOption = {
    value: `${CONTACT_PREFIX}${user.contactId}`,
    label: `My ${pluralNoun}`,
  }

  const teamMember = findTeamMember(user.contactId, teamMembers)
  const teamId = getTeamToDisplayForTeamMember(teamMember)

  if (!isNil(teamId)) {
    const myTeams = getAllTeamsForTeamId(teamId, teams)
    const myTeamMembers = getTeamMembersForTeams(myTeams, teamMembers)
    const myTeamContacts = mapTeamMembersToContacts(myTeamMembers, homeContacts)

    const filteredTeams = myTeams.filter(team => team.id !== teamId)
    const filteredTeamContacts = myTeamContacts.filter(contact => contact.id !== user.contactId)

    const sortedTeams = sortTeams(filteredTeams)
    const sortedTeamContacts = sortContacts(filteredTeamContacts)

    const options = []

    if (showUnallocated && isWholeCompanyManager(teamId))
      options.push(UnallocatedTeamOption)

    options.push(...[
      {
        value: `${TEAM_PREFIX}${teamId}`,
        label: isWholeCompanyManager(teamId) ? 'Whole Company' : 'My Team',
      },
      ...sortedTeams.map(team => {
        return {
          value: `${TEAM_PREFIX}${team.id}`,
          label: team.name,
        }
      }),
      myOption,
      ...sortedTeamContacts.map(contact => {
        return {
          value: `${CONTACT_PREFIX}${contact.id}`,
          label: getContactName(contact),
        }
      }),
    ])

    return options

  } else {
    return [myOption]
  }
}

export const getTeamOrUserOptions = (teamUserOptions, selectedTeamOrUserOptions) => {
  return isEmpty(selectedTeamOrUserOptions) || !teamUserOptions.some(option => selectedTeamOrUserOptions.includes(option.value))
      ? []
      : selectedTeamOrUserOptions
}

export const getTeamOrUserOption = (user, teams, teamMembers, teamUserOptions, selectedTeamOrUserOption) => {
  return isNil(selectedTeamOrUserOption) || !teamUserOptions.some(option => option.value === selectedTeamOrUserOption)
    ? getDefaultTeamOrUserOption(user, teams, teamMembers)
    : selectedTeamOrUserOption
}

export const getTeamOrUserLabel = (teamUserOptions, teamOrUserOption) => {
  const option = teamUserOptions.find(option => option.value === teamOrUserOption)
  return get(option, 'label', '')
}

export const getDefaultTeamOrUserOption = (user, teams, teamMembers) => {
  const teamMember = findTeamMember(user.contactId, teamMembers)
  const teamId = getTeamToDisplayForTeamMember(teamMember)
  return isNil(teamId)
    ? `${CONTACT_PREFIX}${user.contactId}`
    : `${TEAM_PREFIX}${teamId}`
}

export const getContactsForTeamOrUserOption = (teamOrUserOption, teams, teamMembers, homeContacts) => {
  if (isTeamOption(teamOrUserOption)) {
    const teamId = getTeamIdFromOption(teamOrUserOption)
    const allTeamsForOption = getAllTeamsForTeamId(teamId, teams)
    const allTeamMembersForOption = getTeamMembersForTeams(allTeamsForOption, teamMembers)
    const contactIds = allTeamMembersForOption.map(teamMember => teamMember.contactId)
    return homeContacts.filter(contact => contactIds.includes(contact.id))

  } else if (isContactOption(teamOrUserOption)) {
    const contactId = getContactIdFromOption(teamOrUserOption)
    return homeContacts.filter(contact => contact.id === contactId)

  } else {
    return []
  }
}

export const getContactsForSalesAnalysisTeamOrUserOption = (teamOrUserOption, teams, teamMembers, homeContacts) => {
  if (isTeamOption(teamOrUserOption) && isWholeCompanyTeamOrUserOption(teamOrUserOption))
    return homeContacts

  return getContactsForTeamOrUserOption(teamOrUserOption, teams, teamMembers, homeContacts)
}

export const getContactsForSalesAnalysisTeamOrUserOptions = (teamOrUserOptions, teams, teamMembers, homeContacts) => {
  const options = []
  teamOrUserOptions.forEach(teamOrUserOption => {
    options.push(...getContactsForSalesAnalysisTeamOrUserOption(teamOrUserOption, teams, teamMembers, homeContacts))
  })

  return Array.from(new Set(options.map(contact => contact.id)))
}

export const isTeamOption = (teamOrUserOption) => {
  return teamOrUserOption.startsWith(TEAM_PREFIX)
}

export const isContactOption = (teamOrUserOption) => {
  return teamOrUserOption.startsWith(CONTACT_PREFIX)
}

export const getTeamIdFromOption = (teamOrUserOption) => {
  return parseInt(teamOrUserOption.substring(TEAM_PREFIX.length))
}

export const getContactIdFromOption = (teamOrUserOption) => {
  return parseInt(teamOrUserOption.substring(CONTACT_PREFIX.length))
}

export const getTeamToDisplayForTeamMember = (teamMember) => {
  if (isNil(teamMember)) return null
  return !isNil(teamMember.managerTeamId)
    ? teamMember.managerTeamId
    : teamMember.teamId
}

export const sortTeams = (teams) => {
  return sortBy(teams, 'name')
}

export const mapTeamMembersToContacts = (teamMembers, homeContacts) => {
  return teamMembers
      .map(teamMember => mapTeamMemberToContact(teamMember, homeContacts))
      .filter(contact => !isNil(contact))
}

export const mapTeamMemberToContact = (teamMember, homeContacts) => {
  return homeContacts.find(contact => contact.id === teamMember.contactId)
}

export const getAllTeamsForTeamId = (teamId, teams) => {
  if (teamId === WHOLE_COMPANY_TEAM_ID) return teams

  const team = findTeam(teamId, teams)
  return isNil(team)
      ? []
      : getAllTeams(team, teams)
}

export const getAllTeams = (team, teams) => {
  const childTeams = teams
      .filter(childTeam => childTeam.parentId === team.id)
      .map(childTeam => getAllTeams(childTeam, teams))

  return [team, ...flatten(childTeams)]
}

export const findTeamMember = (contactId, teamMembers) => {
  return teamMembers.find(teamMember => teamMember.contactId === contactId)
}

export const findTeam = (teamId, teams) => {
  return teams.find(team => team.id === teamId)
}

export const getTeamMembersForTeams = (teams, teamMembers) => {
  return flatten(teams.map(team => getTeamMembers(team.id, teamMembers)))
}

export const getTeamMembers = (teamId, teamMembers) => {
  return teamMembers
      .filter(teamMember => teamMember.teamId !== null)
      .filter(teamMember => teamMember.teamId === teamId)
}

export const getTeamLicencedUsers = (teamMembers, contactLicences) => {
  return teamMembers
      .filter(teamMember => hasContactMasterLicence(teamMember.contactId, contactLicences) === true)
}

export const hasTeamLicencedUsers = (teamId, teamMembers, contactLicences) => {
  if (isEmpty(teamMembers))
    return false

  const members = getTeamMembers(teamId, teamMembers)
  return !isEmpty(getTeamLicencedUsers(members, contactLicences))
}

export const getTotalUsersWithoutLicence = (teamId, teamMembers, contactLicences) => {
  const members = getTeamMembers(teamId, teamMembers)
  const licencedUsers = getTeamLicencedUsers(members, contactLicences)
  return members.length - licencedUsers.length
}

export const getNoUserLoginMessage = (count) => {
  return count > 1
      ? `${count} users in this team don't have a login`
      : `${count} user in this team doesn't have a login`
}
